/**
 * Generated by orval v8.8.1 🍺
 * Do not edit manually.
 * Points API
 * Point System API
- User point history management
- Admin point adjustment management
- Point history invalidation management
- Point history data is updated not only by web actions but also by point aggregation batch commands
- Related console commands: points:calculate-daily, points:calculate-monthly, points:stats

 * OpenAPI spec version: 1.0.0
 */
import * as zod from "zod";

/**
 * Get point history rows for the target user, enriched with point rule display data.
Returned rows are built from the point history domain that is updated by both immediate application actions and point aggregation batch commands.

 * @summary Get user point histories
 */
export const GetPointHistoriesParams = zod
    .object({
        user_id: zod.number(),
    })
    .strict();

export const getPointHistoriesQueryIncludeInvalidDefault = 0;

export const GetPointHistoriesQueryParams = zod
    .object({
        include_invalid: zod
            .union([zod.literal(0), zod.literal(1)])
            .default(getPointHistoriesQueryIncludeInvalidDefault)
            .describe("Admin-only flag to include invalidated point history rows."),
    })
    .strict();

export const getPointHistories200ResponseHistoriesItemOneRuleTypeMax = 50;

export const getPointHistories200ResponseHistoriesItemOneInvalidatedReasonMax = 255;

export const getPointHistories200ResponseHistoriesItemTwoRuleNameThMax = 100;

export const getPointHistories200ResponseHistoriesItemTwoRuleNameEnMax = 100;

export const getPointHistories200ResponseHistoriesItemTwoRuleNameJaMax = 100;

export const getPointHistories200ResponseHistoriesItemTwoDescriptionThMax = 255;

export const getPointHistories200ResponseHistoriesItemTwoDescriptionEnMax = 255;

export const getPointHistories200ResponseHistoriesItemTwoDescriptionJaMax = 255;

export const GetPointHistories200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        histories: zod.array(
            zod
                .object({
                    point_history_id: zod.number().describe("Point history ID"),
                    transaction_id: zod.number().nullish().describe("Related transaction ID for manual operations"),
                    user_id: zod.number().describe("User ID"),
                    rule_type: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemOneRuleTypeMax)
                        .describe('Rule type. Manual operations use values such as \"adjustment\" and \"redeem\".'),
                    point_date: zod.string().date().describe("Target date for points"),
                    action_count: zod.number().describe("Action execution count"),
                    points_earned: zod.number().describe("Points earned"),
                    daily_point_limit: zod.number().optional().describe("Daily point limit"),
                    is_limit_reached: zod
                        .union([zod.literal(0), zod.literal(1), zod.literal(null)])
                        .nullish()
                        .describe("Limit reached flag"),
                    last_calculated_at: zod
                        .string()
                        .datetime({ offset: true })
                        .optional()
                        .describe("Last calculation timestamp"),
                    created_at: zod.string().datetime({ offset: true }).optional(),
                    updated_at: zod.string().datetime({ offset: true }).optional(),
                    delete_flag: zod.union([zod.literal(0), zod.literal(1)]).describe("Logical invalidation flag"),
                    updated_by: zod.number().nullish().describe("Admin user ID who last updated this history"),
                    general_description: zod
                        .string()
                        .optional()
                        .describe("Manual adjustment or invalidation description"),
                    created_by: zod.number().nullish().describe("Admin user ID who created this history row"),
                    invalidated_at: zod
                        .string()
                        .datetime({ offset: true })
                        .nullish()
                        .describe("Timestamp when this history row was invalidated"),
                    invalidated_by: zod.number().nullish().describe("Admin user ID who invalidated this history row"),
                    invalidated_reason: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemOneInvalidatedReasonMax)
                        .optional()
                        .describe("Reason recorded when this history row was invalidated"),
                    rule_name_th: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemTwoRuleNameThMax)
                        .optional()
                        .describe("Rule name (Thai)"),
                    rule_name_en: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemTwoRuleNameEnMax)
                        .optional()
                        .describe("Rule name (English)"),
                    rule_name_ja: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemTwoRuleNameJaMax)
                        .optional()
                        .describe("Rule name (Japanese)"),
                    description_th: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemTwoDescriptionThMax)
                        .optional()
                        .describe("Description (Thai)"),
                    description_en: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemTwoDescriptionEnMax)
                        .optional()
                        .describe("Description (English)"),
                    description_ja: zod
                        .string()
                        .max(getPointHistories200ResponseHistoriesItemTwoDescriptionJaMax)
                        .optional()
                        .describe("Description (Japanese)"),
                    required_count: zod.number().optional().describe("Required count to complete"),
                    points_per_action: zod.number().optional().describe("Points earned per action for the joined rule"),
                    progress_percentage: zod.number().optional().describe("Progress percentage (0-100)"),
                    is_completed: zod.number().optional().describe("Completion flag"),
                })
                .strict(),
        ),
        can_manage_points: zod
            .boolean()
            .describe("Whether the current authenticated user can adjust or invalidate points."),
    })
    .strict();

export const GetPointHistories403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const GetPointHistories404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const GetPointHistories500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

/**
 * Admin-only endpoint to create a signed manual point adjustment for the target user.
 * @summary Add or subtract points for a user
 */
export const AdjustUserPointsParams = zod
    .object({
        user_id: zod.number(),
    })
    .strict();

export const adjustUserPointsBodyRuleTypeMax = 50;

export const adjustUserPointsBodyNoteMax = 255;

export const AdjustUserPointsBody = zod
    .object({
        amount: zod.number().describe("Signed point amount. Negative values represent deductions."),
        rule_type: zod.string().max(adjustUserPointsBodyRuleTypeMax).describe("Manual rule type for the adjustment"),
        note: zod.string().max(adjustUserPointsBodyNoteMax).optional().describe("Optional operator note"),
    })
    .strict();

export const AdjustUserPoints200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        data: zod
            .object({
                transaction_id: zod.number().nullish(),
                point_history_id: zod.number().optional(),
                balance_after: zod.number().optional(),
                points: zod.number().optional(),
            })
            .strict()
            .optional()
            .describe(
                "Response payload returned after a manual point adjustment creates a transaction and point history row.",
            ),
    })
    .strict();

export const AdjustUserPoints403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const AdjustUserPoints422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const AdjustUserPoints500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

/**
 * Admin-only endpoint to logically invalidate a point history record and append a reversal transaction when needed.
 * @summary Invalidate a point history row
 */
export const InvalidatePointHistoryParams = zod
    .object({
        point_history_id: zod.number(),
    })
    .strict();

export const InvalidatePointHistoryBody = zod
    .object({
        reason: zod.string().describe("Reason for invalidating the point history row"),
    })
    .strict();

export const invalidatePointHistory200ResponseDataInvalidatedReasonMax = 255;

export const InvalidatePointHistory200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        data: zod
            .object({
                point_history_id: zod.number().optional(),
                delete_flag: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                reversal_transaction_id: zod.number().nullish(),
                balance_after: zod.number().optional(),
                invalidated_at: zod.string().datetime({ offset: true }).nullish(),
                invalidated_by: zod.number().nullish(),
                invalidated_reason: zod
                    .string()
                    .max(invalidatePointHistory200ResponseDataInvalidatedReasonMax)
                    .optional(),
            })
            .strict()
            .optional()
            .describe(
                "Response payload returned after invalidating a point history row and optionally appending a reversal transaction.",
            ),
    })
    .strict();

export const InvalidatePointHistory403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const InvalidatePointHistory404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const InvalidatePointHistory422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const InvalidatePointHistory500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(403), zod.literal(404), zod.literal(422), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();
